import React, {
 FunctionComponent, useState, useEffect, useCallback,
 useRef,
} from 'react';
import i18n from 'i18n';
import { AgGridReact } from '@ag-grid-community/react';
import {
  GridReadyEvent,
  IServerSideGetRowsParams,
} from '@ag-grid-enterprise/all-modules';
import './logview.scss';
import { DateRange, DateRangePicker, IDateRangeShortcut } from '@blueprintjs/datetime';
import {
 AnchorButton, Classes, InputGroup, Intent,
 Spinner,
 SpinnerSize
} from '@blueprintjs/core';
import * as logActions from 'data/log/log.actions';
import * as logSelectors from 'data/log/log.selectors';
import { RootState } from 'data/root.reducers';
import { connect, ConnectedProps } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { OpenSearchLogFilterCriteriaDTO } from 'data/log/log.types';
import { getLogs } from 'data/log/log.api';
import { Popover2, Tooltip2 } from '@blueprintjs/popover2';
import moment from 'moment';
import MultiSelectCustom from 'shared/form/custom/MultiSelectCustom';
import { MultiselectTypeahead } from 'shared/form/custom/multiselectTypeahead';
import cloneDeep from 'lodash.clonedeep';
import { logsColumnDefs } from '../log.types';
import exportLogsToFile from '../utils/exportLogs';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  logs: logSelectors.getLogs(state),
  allProcessOptions: logSelectors.getProcessNameOptions(state),
  allSeverityOptions: logSelectors.getSeverityOptions(state),
  allHostnameOptions: logSelectors.getHostnameOptions(state),
  allClusternameOptions: logSelectors.getClusternameOptions(state),
});

const mapDispatch = {
  getAllLogs: logActions.getAllLogs,
  exportLogs: logActions.exportLogs,
  getFilterOptions: logActions.getFilterOptions,
  setFilterOptions: logActions.setFilterOptions,
};

const PAGE_SIZE = 100;

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

const connector = connect(mapState, mapDispatch);

export const LogView: FunctionComponent<Props> = ({
 logs, allProcessOptions, allSeverityOptions, allHostnameOptions, allClusternameOptions, setFilterOptions, getFilterOptions
}: Props) => {
  const gridRef = useRef<any>(null);

  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);

  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);

  const [selectedDateRange, setSelectedDateRange] = useState<DateRange>([startOfDay, endOfDay]);
  const [selectedShortcutIndex, setSelectedShortcutIndex] = useState<number>(0);
  const [dateRangePopupOpen, setDateRangePopupOpen] = useState<boolean>(false);
  const [dateRangeButtonText, setDateRangeButtonText] = useState<string>('Today');

  const [processNameMultiSelectOptions, setProcessNameMultiSelectOptions] = useState<MultiselectTypeahead[]>([]);
  const [processNamesAllOptions, setProcessNamesAllOptions] = useState<MultiselectTypeahead[]>([]);

  const [severityMultiSelectOptions, setSeverityMultiSelectOptions] = useState<MultiselectTypeahead[]>([]);
  const [severityAllOptions, setSeverityAllOptions] = useState<MultiselectTypeahead[]>([]);

  const [hostnameMultiSelectOptions, setHostnameMultiSelectOptions] = useState<MultiselectTypeahead[]>([]);
  const [hostnameAllOptions, setHostnameAllOptions] = useState<MultiselectTypeahead[]>([]);

  const [clusternameMultiSelectOptions, setClusternameMultiSelectOptions] = useState<MultiselectTypeahead[]>([]);
  const [clusternameAllOptions, setClusternameAllOptions] = useState<MultiselectTypeahead[]>([]);

  const [quickFilterText, setQuickFilterText] = useState<string>('');
  const [isExporting, setIsExporting] = useState(false);

  const [debouncedFilterText, setDebouncedFilterText] = useState('');

  useEffect(() => {
    getFilterOptions(buildFilterCriteria(selectedDateRange, [], [], [], [], ''));
  }, [getFilterOptions]);

  useEffect(() => {
    if (allProcessOptions && allProcessOptions.length > 0) {
      setProcessNamesAllOptions(
        allProcessOptions.map((o) => {
          return {
            label: o.key,
            value: o.key
          };
        })
      );
    }
    if (allSeverityOptions && allSeverityOptions.length > 0) {
      setSeverityAllOptions(
        allSeverityOptions.map((o) => {
          return {
            label: o.key,
            value: o.key
          };
      }));
    }

    if (allHostnameOptions && allHostnameOptions.length > 0) {
      setHostnameAllOptions(
        allHostnameOptions.map((o) => {
          return {
            label: o.key,
            value: o.key
          };
      }));
    }

    if (allClusternameOptions && allClusternameOptions.length > 0) {
      setClusternameAllOptions(
        allClusternameOptions.map((o) => {
          return {
            label: o.key,
            value: o.key
          };
      }));
    }
  }, [allProcessOptions, allSeverityOptions, allHostnameOptions, allClusternameOptions]);

  useEffect(() => {
    const delayInputTimeoutId = setTimeout(() => {
      setDebouncedFilterText(quickFilterText);
    }, 500);
    return () => clearTimeout(delayInputTimeoutId);
  }, [quickFilterText]);

  const buildFilterCriteria = (
    dateRange: DateRange,
    selectedProcessNames: { label: string; value: string }[],
    selectedSeverities: { label: string; value: string }[],
    selectedHostname: { label: string; value: string }[],
    selectedClusternames: { label: string; value: string }[],
    textSearch: string
  ): OpenSearchLogFilterCriteriaDTO => {
    return {
      dateRange: [dateRange[0] as Date, dateRange[1] as Date],
      processNames: (selectedProcessNames ?? []).map((x) => x.value),
      severities: (selectedSeverities ?? []).map((x) => x.value),
      hostnames: (selectedHostname ?? []).map((x) => x.value),
      clusternames: (selectedClusternames ?? []).map((x) => x.value),
      textSearch
    };
  };

  const handleRefresh = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.refreshServerSideStore({ purge: true });
    }
  };

  const handleShortcutChange = (shortcut: IDateRangeShortcut, index: number) => {
    setSelectedShortcutIndex(index);
    setDateRangeButtonText(shortcut.label);
  };

  const handleDateChange = (dateRange: DateRange) => {
    setSelectedDateRange(dateRange);
    if (!dateRange[0] || !dateRange[1]) {
      return;
    }
    if (gridRef.current) {
      setDateRangeButtonText(`${moment(dateRange[0]).format('lll')} - ${moment(dateRange[1]).format('lll')}`);
      setDateRangePopupOpen(false);
    }

    getFilterOptions(buildFilterCriteria(dateRange, [], [], [], [], ''));
  };

  const handleInteraction = (isOpen: boolean) => {
    setDateRangePopupOpen(isOpen);
  }

  const handleExport = () => {
    setIsExporting(true);
    exportLogsToFile(
      buildFilterCriteria(
        selectedDateRange,
        processNameMultiSelectOptions,
        severityMultiSelectOptions,
        hostnameMultiSelectOptions,
        clusternameMultiSelectOptions,
        quickFilterText
      ),
      setIsExporting
    );
  }

  const handleProcessNameSelectChange = (name: string, value: { label: string; value: string }[]) => {
    const selected = cloneDeep(value);
    setProcessNameMultiSelectOptions(selected);
  }

  const handleSeveritySelectChange = (name: string, value: { label: string; value: string; isPlaceholder?: boolean; }[]) => {
    const selected = cloneDeep(value);
    setSeverityMultiSelectOptions(selected);
  }

  const handleHostnameSelectChange = (name: string, value: { label: string; value: string; isPlaceholder?: boolean; }[]) => {
    const selected = cloneDeep(value);
    setHostnameMultiSelectOptions(selected);
  }

  const handleClusternameSelectChange = (name: string, value: { label: string; value: string; isPlaceholder?: boolean; }[]) => {
    const selected = cloneDeep(value);
    setClusternameMultiSelectOptions(selected);
  }
  const handleQuickFilterChange = (e: React.FormEvent<HTMLElement>) => {
    setQuickFilterText((e.target as HTMLInputElement).value);
  }

  const getServerSideDataSource = useCallback(() => {
    return {
      getRows(params: IServerSideGetRowsParams) {
        const { request } = params;
        const filterCriteria = buildFilterCriteria(
          selectedDateRange,
          processNameMultiSelectOptions,
          severityMultiSelectOptions,
          hostnameMultiSelectOptions,
          clusternameMultiSelectOptions,
          debouncedFilterText
        );
        getLogs(PAGE_SIZE, request.startRow || 0, filterCriteria)
          .then((r) => {
            const logs = r.data?.data || [];
            const count = r.data?.metadata?.recordCount || 0;
            params.success({ rowData: logs, rowCount: count });
          })
          .catch((r) => {
            params.fail();
          });
      }
    };
  }, [
    selectedDateRange,
    processNameMultiSelectOptions,
    severityMultiSelectOptions,
    hostnameMultiSelectOptions,
    clusternameMultiSelectOptions,
    debouncedFilterText
  ]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setServerSideDatasource(getServerSideDataSource());
    }
  }, [getServerSideDataSource]);

  const onGridReady = useCallback((grid: GridReadyEvent) => {
    grid.api.setServerSideDatasource(getServerSideDataSource());
  }, []);

  return (
    <div className="row logview-container" style={{ height: 'inherit' }}>
      <div className="formPanel">
        <h2 className="formPanelTitle">Log Monitoring</h2>
      </div>
      <div className="col-md-12" style={{ height: 'inherit' }}>
        <div className="form-flex-row-css">
          <InputGroup
            style={{ marginRight: '8px', width: '400px' }}
            onChange={handleQuickFilterChange}
            type="text"
            leftIcon="search"
            placeholder={i18n.t('common.grid.search')}
            value={quickFilterText}
          />

          <div className="log-filter-process-name">
            <MultiSelectCustom
              items={processNamesAllOptions}
              fieldValue={processNameMultiSelectOptions}
              name="processName"
              placeholder="Process Name"
              onItemSelect={handleProcessNameSelectChange}
              dataTestName=""
              disabled={false}
            />
          </div>
          <div className="log-filter-severity">
            <MultiSelectCustom
              className="log-filter-severity"
              items={severityAllOptions}
              fieldValue={severityMultiSelectOptions}
              name="severity"
              placeholder="Severity"
              onItemSelect={handleSeveritySelectChange}
              dataTestName=""
              disabled={false}
            />
          </div>
          <div className="log-filter-hostname">
            <MultiSelectCustom
              className="log-filter-hostname"
              items={hostnameAllOptions}
              fieldValue={hostnameMultiSelectOptions}
              name="hostname"
              placeholder="Hostname"
              onItemSelect={handleHostnameSelectChange}
              dataTestName=""
              disabled={false}
            />
          </div>
          <div className="log-filter-clustername">
            <MultiSelectCustom
              className="log-filter-clustername"
              items={clusternameAllOptions}
              fieldValue={clusternameMultiSelectOptions}
              name="clustername"
              placeholder="Clustername"
              onItemSelect={handleClusternameSelectChange}
              dataTestName=""
              disabled={false}
            />
          </div>
          <div className="log-filter-daterange">
            <Popover2
              interactionKind="click"
              placement="bottom"
              popoverClassName={Classes.POPOVER_CONTENT_SIZING}
              isOpen={dateRangePopupOpen}
              onInteraction={handleInteraction}
              minimal
              content={
                (
                  <DateRangePicker
                    allowSingleDayRange
                    selectedShortcutIndex={selectedShortcutIndex}
                    value={selectedDateRange}
                    onChange={handleDateChange}
                    onShortcutChange={handleShortcutChange}
                    timePickerProps={
                      {
                        useAmPm: true
                      }
                    }
                  />
                )
              }
            >
              <AnchorButton
                icon="calendar"
                rightIcon="caret-down"
                intent={Intent.PRIMARY}
                text={dateRangeButtonText}
              />
            </Popover2>
          </div>
          <div className="flex-spacer" />
          <Tooltip2 content="Export logs to file">
            <AnchorButton
              icon={isExporting ? <Spinner size={SpinnerSize.SMALL} /> : 'export'}
              intent={Intent.NONE}
              text="Export"
              disabled={isExporting}
              onClick={handleExport}
            />
          </Tooltip2>
          <div>
            <AnchorButton
              icon="refresh"
              intent={Intent.NONE}
              text="Refresh"
              onClick={handleRefresh}
            />
          </div>
        </div>
        <div className="ag-theme-balham common-grid-container">
          <AgGridReact
            ref={gridRef}
            columnDefs={logsColumnDefs}
            pagination
            paginationPageSize={PAGE_SIZE}
            rowModelType="serverSide"
            serverSideStoreType="partial"
            cacheBlockSize={PAGE_SIZE}
            onGridReady={onGridReady}
          />
        </div>
      </div>
    </div>
  );
};

export default withRouter((connector(LogView)));
